import { api } from "@/services/api"
import { publicApi } from "@/services/publicApi"

// ── Types ────────────────────────────────────────────────────────────
// Mirror BookingSeason.cs in Cms.Services. Unlike most AMS enums these two arrive by name, not by
// number: the server serializes them with JsonStringEnumConverter.

/** BookingSeasonStatus. Open exactly when `opensAt <= now < closesAt`. */
export type BookingSeasonStatus = "Open" | "Closed"

/**
 * BookingSeasonClosedReason. Staff only — the public sees one message whatever the reason.
 * AllProgramsDisabled: open by its dates, but every bookable funding program (not SHEAP) is switched off (M9).
 */
export type BookingSeasonClosedReason = "NotConfigured" | "NotYetOpen" | "Ended" | "AllProgramsDisabled"

/** AmsFundingProgram, by name like the two enums above. */
export type AmsFundingProgram = "Liheap" | "PseHelpGas" | "PseHelpElectric" | "Sheap"

/** FundingProgramState: one funding program's current switch, with its audit. Staff only. */
export interface FundingProgramState {
  program: AmsFundingProgram
  isEnabled: boolean
  /** What the public is told while the program is off: the saved message, or the program's default. */
  message: string
  /** UPN. Null when the switch has never been saved. */
  createdBy: string | null
  /** UTC. Null when the switch has never been saved. */
  createdAt: string | null
}

/** PublicFundingProgramStatus: one funding program as the public booking pages see it. No audit. */
export interface PublicFundingProgramStatus {
  program: AmsFundingProgram
  isEnabled: boolean
  message: string
}

/** BookingSeasonSnapshot: GET /api/ams/booking-season, and the body of a successful save or close-now. */
export interface BookingSeasonSnapshot {
  status: BookingSeasonStatus
  /** Null while Open. */
  closedReason: BookingSeasonClosedReason | null
  /** ISO instant with the Pacific offset, e.g. "2026-10-01T00:00:00-07:00". Null when no season is set. */
  opensAt: string | null
  /** Exclusive. Null when no season is set. */
  closesAt: string | null
  closedWebMessage: string
  closedPhoneMessageEnglish: string
  /** Null plays the English text to Spanish callers. */
  closedPhoneMessageSpanish: string | null
  /** UPN. Null when no season is set. */
  createdBy: string | null
  /** UTC. Null when no season is set. */
  createdAt: string | null
  /**
   * The appointments window: the earliest slot start public web and phone booking may be booked into
   * (inclusive), as an ISO instant with the Pacific offset. Null for no lower limit. Optional because an
   * older API does not send it, which means no limit.
   */
  firstAppointmentAt?: string | null
  /** No public appointment starts at or after this (exclusive). Null for no upper limit. */
  appointmentsEndAt?: string | null
  /**
   * Every funding program's switch (M9); all on until one is saved. Optional because a save or a
   * close-now answers with the season alone.
   */
  programs?: FundingProgramState[]
}

/** BookingSeasonDraft: the body of PUT /api/ams/booking-season. */
export interface SaveBookingSeasonRequest {
  /** ISO instant with an explicit offset — build it from Pacific wall time, not the browser's zone. */
  opensAt: string
  closesAt: string
  closedWebMessage: string
  closedPhoneMessageEnglish: string
  closedPhoneMessageSpanish: string | null
  /** Optional appointments window, Pacific instants with explicit offsets. Null means no limit on that side. */
  firstAppointmentAt: string | null
  appointmentsEndAt: string | null
}

/** The body of a 400 from PUT: `saved` is always null then. */
export interface BookingSeasonSaveRefusal {
  saved: null
  errors: string[]
}

/** PublicBookingSeasonStatus: GET /api/ams/booking-season/public. No dates, no reason, no audit. */
export interface PublicBookingSeasonStatus {
  status: BookingSeasonStatus
  /** The season's website message while Closed; null while Open. */
  message: string | null
  /** Every funding program, whether it is on, and what to say while it is off (M9). */
  programs?: PublicFundingProgramStatus[]
  /**
   * The AmsBookingWithoutIncome flag (M18, AB#840): the flow skips the household income question.
   * Absent from an older API, which means off.
   */
  amsBookingWithoutIncome?: boolean
}

/** The `code` on a 409 from reserve or create when public booking is closed. */
export const BOOKING_SEASON_CLOSED_CODE = "bookingSeasonClosed"

// ── Staff endpoints (Entra token) ────────────────────────────────────

const taggedApi = api.enhanceEndpoints({ addTagTypes: ["AmsBookingSeason"] })

export const bookingSeasonApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Requires appointments:view. */
    getBookingSeason: builder.query<BookingSeasonSnapshot, void>({
      query: () => ({ url: "/api/ams/booking-season" }),
      providesTags: ["AmsBookingSeason"],
    }),
    /** Requires appointments:manage-booking-season. 400 carries a BookingSeasonSaveRefusal. */
    saveBookingSeason: builder.mutation<BookingSeasonSnapshot, SaveBookingSeasonRequest>({
      query: (body) => ({ url: "/api/ams/booking-season", method: "PUT", body }),
      invalidatesTags: ["AmsBookingSeason"],
    }),
    /** Requires appointments:manage-booking-season. A no-op when booking is already closed. */
    closeBookingSeasonNow: builder.mutation<BookingSeasonSnapshot, void>({
      query: () => ({ url: "/api/ams/booking-season/close-now", method: "POST" }),
      invalidatesTags: ["AmsBookingSeason"],
    }),
  }),
})

export const {
  useGetBookingSeasonQuery,
  useSaveBookingSeasonMutation,
  useCloseBookingSeasonNowMutation,
} = bookingSeasonApi

// ── Public endpoint (booking-session token) ──────────────────────────

export const publicBookingSeasonApi = publicApi.injectEndpoints({
  endpoints: (builder) => ({
    getPublicBookingSeason: builder.query<PublicBookingSeasonStatus, void>({
      query: () => ({ url: "/api/ams/booking-season/public" }),
    }),
  }),
})

export const { useGetPublicBookingSeasonQuery } = publicBookingSeasonApi
